import { ErrorHandler, InjectionToken, NgModule } from "@angular/core";
import { BrowserModule, Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { OverlayContainer } from "@angular/cdk/overlay";
import { CustomOverlayContainer } from "./theme/utils/custom-overlay-container";
import { CommonModule, DatePipe } from "@angular/common";
import { DecimalPipe } from "@angular/common";
import { PerfectScrollbarModule } from "ngx-perfect-scrollbar";
import { PERFECT_SCROLLBAR_CONFIG } from "ngx-perfect-scrollbar";
import { PerfectScrollbarConfigInterface } from "ngx-perfect-scrollbar";

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    wheelPropagation: true,
    suppressScrollX: true,
};

import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { CalendarModule, DateAdapter } from "angular-calendar";
import { adapterFactory } from "angular-calendar/date-adapters/date-fns";
import { SharedModule } from "./shared/shared.module";
import { PipesModule } from "./theme/pipes/pipes.module";
import { routing } from "./app.routing";

import { AppSettings } from "./app.settings";
import { AppComponent, IMG_URL } from "./app.component";
import { PagesComponent } from "./pages/pages.component";
import { BlankComponent } from "./pages/blank/blank.component";
import { NotFoundComponent } from "./pages/errors/not-found/not-found.component";
import { ErrorComponent } from "./pages/errors/error/error.component";

import { SidenavComponent } from "./theme/components/sidenav/sidenav.component";
import { VerticalMenuComponent } from "./theme/components/menu/vertical-menu/vertical-menu.component";
import { HorizontalMenuComponent } from "./theme/components/menu/horizontal-menu/horizontal-menu.component";
import { FlagsMenuComponent } from "./theme/components/flags-menu/flags-menu.component";
import { FullScreenComponent } from "./theme/components/fullscreen/fullscreen.component";
import { ApplicationsComponent } from "./theme/components/applications/applications.component";
import { MessagesComponent } from "./theme/components/messages/messages.component";
import { UserMenuComponent } from "./theme/components/user-menu/user-menu.component";
import { FavoritesComponent } from "./theme/components/favorites/favorites.component";
import { EquipmentListComponent } from "./pages/equipment/equipment-list/equipment-list.component";
import { LocationListComponent } from "./pages/location-list/location-list.component";
import { CustomHttpInterceptor } from "./http-interceptor";
import { CylinderIconComponent } from "./pages/equipment/icons/cylinder-icon/cylinder-icon.component";
import { TankIconComponent } from "./pages/equipment/icons/tank-icon/tank-icon.component";
import { BlankTankIconComponent } from "./pages/equipment/icons/blank-tank-icon/blank-tank-icon.component";
import { FuelOilIconComponent } from "./pages/equipment/icons/fuel-oil-icon/fuel-oil-icon.component";
import { UndergroundTankIconComponent } from "./pages/equipment/icons/underground-tank-icon/underground-tank-icon.component";
import { ApplianceIconComponent } from "./pages/equipment/icons/appliance-icon/appliance-icon.component";
import { RadioWavesIconComponent } from "./pages/equipment/icons/radio-waves-icon/radio-waves.component";
import { MeterIconComponent } from "./pages/equipment/icons/meter-icon/meter-icon.component";
import { RegulatorIconComponent } from "./pages/equipment/icons/regulator-icon/regulator-icon.component";
import { MiscellaneousIconComponent } from "./pages/equipment/icons/miscellaneous-icon/miscellaneous-icon.component";
import { IconSelectorComponent } from "./pages/equipment/icons/icon-selector/icon-selector.component";

import { ContractsComponent } from "./pages/contracts/contracts.component";
import { DonutChartComponent } from "./pages/contracts/donut-chart/donut-chart.component";
import { ContractsCardComponent } from "./pages/contracts/contracts-card/contracts-card.component";
import { TransactionsComponent } from "./pages/transactions/transactions.component";
import { TransactionsCardComponent } from "./pages/transactions/transactions-card/transactions-card.component";
import { TransactionsDialogComponent } from "./pages/transactions/transactions-dialog/transactions-dialog-component";
import { TransactionDialogCardComponent } from "./pages/transactions/transactions-dialog-card/transaction-dialog-card.component";
import { NotesDialogComponent } from "./pages/transactions/notes-dialog/notes-dialog.component";
import { MatBadgeModule } from "@angular/material/badge";
import { ExcelService } from "./services/excel-service";
import { ExportDataComponent } from "./pages/transactions/export-data/export-data.component";
import { AuthInterceptor } from "./services/auth-interceptor";
import { CustomerSearchComponent } from "./pages/customer-search/customer-search.component";
import { AutofocusFixModule } from "ngx-autofocus-fix";
import { SummaryComponent } from "./pages/summary/summary.component";
import { RecentActivityComponent } from "./pages/summary/recent-activity/recent-activity.component";
import { RecentActivityItemComponent } from "./pages/summary/recent-activity-item/recent-activity-item.component";
import
    {
        AccountDetailsComponent,
        BankItemComponent,
        CardItemComponent,
        ContactDialogComponent,
        ContactListComponent,
        ContactListItemComponent,
        PaymentMethodListComponent,

    } from "./pages/account-details";
import { ConfirmDialogComponent } from "./shared/confirm-dialog";
import { ContactUsComponent } from "./pages/contact-us/contact-us.component";
import { LocationListCardComponent } from "./pages/location-list/location-list-card/location-list-card.component";
import { API_BASE_URL } from "./models";
import { environment } from "../environments/environment";
import { NewPaymentMethodDialogComponent } from "./pages/account-details/new-payment-method-dialog/new-payment-method-dialog.component";
import { CardTemplateComponent } from "./pages/account-details/card-template/card-template.component";
import { SummaryProgressBarComponent } from "./pages/summary/summary-progress-bar/summary-progress-bar.component";
import { RequestServiceDialogComponent } from "./pages/summary/request-service-dialog/request-service-dialog.component";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { RequestFormComponent } from "./pages/summary/request-form/request-form.component";
import { RequestDeliveryDialogComponent } from "./pages/summary/request-delivery-dialog/request-delivery-dialog.component";
import { RequestDialogHeaderComponent } from "./pages/summary/request-dialog-header/request-dialog-header.component";
import { RequestConfirmationDialogComponent } from "./pages/summary/request-form/request-confirmation-dialog/request-confirmation-dialog.component";
import { ApsModule } from "./pages/aps/aps.module";
import { PrebuyComponent } from "./pages/prebuy/prebuy.component";
import { PrebuyContractComponent } from "./pages/prebuy/prebuy-contract/prebuy-contract.component";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { UpdatePasswordDialogComponent } from "./pages/account-details/update-password-dialog/update-password-dialog.component";
import { MatRadioModule } from "@angular/material/radio";
import { AccountCreditPmtDialogComponent } from "./pages/prebuy/account-credit-pmt-dialog/account-credit-pmt-dialog.component";
import { RegisterComponent } from "./pages/register/register.component";
import { RegConfirmDialogComponent } from "./pages/register/reg-confirm-dialog/reg-confirm-dialog.component";
import { SetPasswordComponent } from "./pages/set-password/set-password.component";
import { ConfirmSetPwDialogComponent } from "./pages/set-password/confirm-set-pw-dialog/confirm-set-pw-dialog.component";
import { PrintPaymentReceiptComponent } from "./pages/aps/make-a-payment/payment-submitted-dialog/print-payment-receipt/print-payment-receipt.component";
import { CustomerAgingDialogComponent } from "./pages/summary/customer-aging-dialog/customer-aging-dialog.component";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { ErrorDialogComponent } from "./components/error-dialog/error-dialog.component";
import { AccountCreditSuccessDialogComponent } from "./pages/prebuy/account-credit-success-dialog/account-credit-success-dialog.component";
import { ProcessingDialogComponent } from "./components/processing-dialog/processing-dialog.component";
import { CustomErrorHandler } from "./shared/custom-error-handler/custom-error-handler";
import { ErrorLoggingService } from "./services/error-logging.service";
import { AnnouncementsFrameComponent } from "./pages/summary/announcements-frame/announcements-frame.component";
import { EmailListDialogComponent } from "./pages/register/email-list-dialog/email-list-dialog.component";
import { PromptDialogComponent } from "./components/prompt-dialog/prompt-dialog.component";
import { AlertDialogComponent } from "./components/alert-dialog/alert-dialog.component";
import { NgxMaskModule, IConfig } from "ngx-mask";
import { FuelTankComponent } from "./components/fuel-tank/fuel-tank.component";
import { ContactListDialogComponent } from "./pages/account-details/contact-list-dialog/contact-list-dialog.component";
import { PendingDeliveryDialogComponent } from "./pages/summary/pending-delivery/pending-delivery-dialog.component";
import { AutoPayComponent } from "./pages/account-details/auto-pay/auto-pay.component";
import { AutoPayRequestDialogComponent } from "./pages/account-details/auto-pay-request-dialog/auto-pay-request-dialog.component";
import { EditCardDialogComponent } from "./pages/aps/edit-card-dialog/edit-card-dialog.component";
import { EditBankDialogComponent } from "./pages/aps/edit-bank-dialog/edit-bank-dialog.component";
import { TempPasswordEmailDialogComponent } from "./pages/register/temp-password-email-dialog/temp-password-email-dialog.component";
import { ForgotPasswordComponent } from "./pages/forgot-password/forgot-password.component";
import { SearchFilterDialogComponent } from "./pages/customer-search/search-filter-dialog/search-filter-dialog.component";
import { PriceCalculatorComponent } from "./components/price-calculator/price-calculator.component";
import { SessionTimeoutDialogComponent } from "./components/session-timeout-dialog/session-timeout-dialog.component";
import { LoggedOutDialogComponent } from "./components/logged-out-dialog/logged-out-dialog.component";
import { SearchBarComponent } from "./components/search-bar/search-bar.component";
import { ImagingComponent } from "./pages/imaging/imaging.component";
import { VerifyEmployeeDialogComponent } from "./components/verify-employee-dialog/verify-employee-dialog.component";
import { ExternalLoginComponent } from "./pages/external-login/external-login.component";
@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        // AgmCoreModule.forRoot({
        //    apiKey: "AIzaSyDLf9Ywk47zipEtorDewwMmB3JtuXdzYL4",
        // }),
        PerfectScrollbarModule,
        CalendarModule.forRoot({
            provide: DateAdapter,
            useFactory: adapterFactory,
        }),
        SharedModule,
        PipesModule,
        routing,
        HttpClientModule,
        AutofocusFixModule.forRoot(),
        MatBadgeModule,
        ApsModule,
        MatDialogModule,
        MatIconModule,
        ScrollingModule,
        CommonModule,
        MatRadioModule,
        DragDropModule,
        NgxMaskModule.forRoot(),

    ],
    declarations: [
        AppComponent,
        PagesComponent,
        BlankComponent,
        NotFoundComponent,
        ErrorComponent,
        SidenavComponent,
        VerticalMenuComponent,
        HorizontalMenuComponent,
        FlagsMenuComponent,
        FullScreenComponent,
        ApplicationsComponent,
        MessagesComponent,
        UserMenuComponent,
        FavoritesComponent,
        EquipmentListComponent,
        LocationListComponent,
        CylinderIconComponent,
        TankIconComponent,
        BlankTankIconComponent,
        FuelOilIconComponent,
        UndergroundTankIconComponent,
        ApplianceIconComponent,
        RadioWavesIconComponent,
        MeterIconComponent,
        RegulatorIconComponent,
        MiscellaneousIconComponent,
        IconSelectorComponent,
        CustomerSearchComponent,
        SummaryComponent,
        RecentActivityComponent,
        RecentActivityItemComponent,
        AccountDetailsComponent,
        ContactDialogComponent,
        ContactListItemComponent,
        ContactListComponent,
        ConfirmDialogComponent,
        ContactUsComponent,
        PaymentMethodListComponent,
        LocationListCardComponent,
        ContractsComponent,
        DonutChartComponent,
        ContractsCardComponent,
        TransactionsCardComponent,
        TransactionsComponent,
        TransactionsDialogComponent,
        TransactionDialogCardComponent,
        NotesDialogComponent,
        ExportDataComponent,
        BankItemComponent,
        CardItemComponent,
        NewPaymentMethodDialogComponent,
        CardTemplateComponent,
        SummaryProgressBarComponent,
        RequestServiceDialogComponent,
        RequestFormComponent,
        RequestDeliveryDialogComponent,
        RequestDialogHeaderComponent,
        RequestConfirmationDialogComponent,
        PendingDeliveryDialogComponent,
        PrebuyComponent,
        PrebuyContractComponent,
        UpdatePasswordDialogComponent,
        AccountCreditPmtDialogComponent,
        RegisterComponent,
        RegConfirmDialogComponent,
        SetPasswordComponent,
        ConfirmSetPwDialogComponent,
        CustomerAgingDialogComponent,
        ErrorDialogComponent,
        AccountCreditSuccessDialogComponent,
        ProcessingDialogComponent,
        AnnouncementsFrameComponent,
        EmailListDialogComponent,
        PromptDialogComponent,
        AlertDialogComponent,
        FuelTankComponent,
        ContactListDialogComponent,
        AutoPayComponent,
        AutoPayRequestDialogComponent,
        TempPasswordEmailDialogComponent,
        VerifyEmployeeDialogComponent,
        ForgotPasswordComponent,
        SearchFilterDialogComponent,
        SessionTimeoutDialogComponent,
        PriceCalculatorComponent,
        LoggedOutDialogComponent,
        SearchBarComponent,
        ImagingComponent,
        ExternalLoginComponent
    

    ],
    entryComponents: [
        VerticalMenuComponent,
        ContactDialogComponent,
        ConfirmDialogComponent,
        TransactionsDialogComponent,
        NotesDialogComponent,
        NewPaymentMethodDialogComponent,
        PendingDeliveryDialogComponent,
        RequestServiceDialogComponent,
        RequestDeliveryDialogComponent,
        RequestConfirmationDialogComponent,
        UpdatePasswordDialogComponent,
        AccountCreditPmtDialogComponent,
        RegConfirmDialogComponent,
        ConfirmSetPwDialogComponent,
        PrintPaymentReceiptComponent,
        CustomerAgingDialogComponent,
        ErrorDialogComponent,
        AccountCreditSuccessDialogComponent,
        ProcessingDialogComponent,
        EmailListDialogComponent,
        PromptDialogComponent,
        AlertDialogComponent,
        ContactListDialogComponent,
        SessionTimeoutDialogComponent,
        LoggedOutDialogComponent,
        AutoPayComponent,
        AutoPayRequestDialogComponent,
        TempPasswordEmailDialogComponent,
        VerifyEmployeeDialogComponent,
        ForgotPasswordComponent,
        SearchFilterDialogComponent

    ],
    providers: [
        AppSettings,
        CustomHttpInterceptor,
        ExcelService,
        ErrorLoggingService,
        DatePipe,
        Title,
        DecimalPipe,
        { provide: PERFECT_SCROLLBAR_CONFIG, useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG },
        { provide: OverlayContainer, useClass: CustomOverlayContainer },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CustomHttpInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        { provide: API_BASE_URL, useValue: environment.baseServiceURL },
        { provide: IMG_URL, useValue: environment.iconUrl },
        { provide: ErrorHandler, useClass: CustomErrorHandler }
    ],
    bootstrap: [
        AppComponent,
    ],
    exports: [
        TransactionsDialogComponent,
        SharedModule,


    ],
})

export class AppModule { }
